// Файл frontend_logger.js

/**
 * Модуль обслуживает сценарий `frontend_logger` в разделе `utils`.
 * Он содержит обработчики и данные, относящиеся к этой части клиентского
 * интерфейса.
 * Код связывает локальную логику с соседними компонентами без
 * дублирования общих правил.
 * Такое разделение упрощает сопровождение, проверку поведения и
 * дальнейшее расширение.
 */

const browser_window = typeof window === 'undefined' ? null : window;
const browser_navigator = typeof navigator === 'undefined'
    ? null
    : navigator;
const browser_location = browser_window?.location || {
    hostname: '127.0.0.1',
    origin: 'http://127.0.0.1:57272',
    pathname: '/',
};
const IS_LOCAL_FRONTEND = [
    '127.0.0.1',
    'localhost',
].includes(browser_location.hostname);
const API_BASE_URL = IS_LOCAL_FRONTEND
    ? 'http://127.0.0.1:57272'
    : browser_location.origin;
const FRONTEND_LOG_PATH = '/api/frontend_log';
const DUPLICATE_WINDOW_MS = 10000;

let frontend_csrf_token = '';
let frontend_csrf_request = null;
let frontend_logging_installed = false;
const recent_error_fingerprints = new Map();



const safe_text = (value, limit = 12000) => (
    String(value || '')
        .replace(/\0/g, '')
        .slice(0, Math.max(1, Number(limit || 12000)))
);

const error_details = (error) => {
    if (error instanceof Error) {
        return {
            message: safe_text(error.message, 4000),
            stack: safe_text(error.stack, 20000),
        };
    }

    if (typeof error === 'object' && error !== null) {
        try {
            return {
                message: safe_text(
                    error.message || JSON.stringify(error),
                    4000
                ),
                stack: safe_text(error.stack, 20000),
            };

        } catch (_error) {
            return {
                message: safe_text(error, 4000),
                stack: '',
            };
        }
    }

    return {
        message: safe_text(error, 4000),
        stack: '',
    };
};

const is_duplicate_error = (payload) => {
    const fingerprint = [
        payload.event_type,
        payload.message,
        payload.stack,
        payload.request_path,
        payload.http_status,
    ].join('|').slice(0, 4000);
    const current_time = Date.now();
    const previous_time = recent_error_fingerprints.get(fingerprint) || 0;
    recent_error_fingerprints.set(fingerprint, current_time);

    for (const [key, timestamp] of recent_error_fingerprints.entries()) {
        if (current_time - timestamp > DUPLICATE_WINDOW_MS * 3) {
            recent_error_fingerprints.delete(key);
        }
    }

    return current_time - previous_time < DUPLICATE_WINDOW_MS;
};

const load_frontend_csrf_token = async () => {
    if (frontend_csrf_token) {
        return frontend_csrf_token;
    }

    if (!frontend_csrf_request) {
        frontend_csrf_request = fetch(
            `${API_BASE_URL}/api/csrf_token`,
            {
                method: 'GET',
                credentials: 'include',
                cache: 'no-store',
            }
        ).then(async (response) => {
            if (!response.ok) {
                throw new Error('Не удалось получить CSRF-токен журнала');
            }

            const data = await response.json();
            frontend_csrf_token = safe_text(data?.csrf_token, 200);

            if (!frontend_csrf_token) {
                throw new Error('Пустой CSRF-токен журнала');
            }

            return frontend_csrf_token;
        }).finally(() => {
            frontend_csrf_request = null;
        });
    }

    return frontend_csrf_request;
};

const send_frontend_log = async (payload) => {
    try {
        const csrf_token = await load_frontend_csrf_token();
        const response = await fetch(
            `${API_BASE_URL}${FRONTEND_LOG_PATH}`,
            {
                method: 'POST',
                credentials: 'include',
                keepalive: true,
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRF-Token': csrf_token,
                },
                body: JSON.stringify(payload),
            }
        );

        if (response.status === 403) {
            frontend_csrf_token = '';
        }

    } catch (_error) {
        // Ошибка отправки журнала не должна порождать новую ошибку журнала.
    }
};

const log_frontend_error = (
    event_type,
    error,
    details = {}
) => {
    if (!browser_window) {
        return;
    }

    const normalized_error = error_details(error);
    const payload = {
        level: safe_text(details.level || 'ERROR', 20).toUpperCase(),
        event_type: safe_text(event_type, 80),
        message: normalized_error.message,
        stack: normalized_error.stack,
        component_stack: safe_text(details.component_stack, 12000),
        page_path: safe_text(browser_location.pathname, 500),
        request_method: safe_text(details.request_method, 20),
        request_path: safe_text(details.request_path, 800),
        http_status: Number(details.http_status || 0),
        browser: safe_text(browser_navigator?.userAgent, 800),
        release_id: safe_text(
            browser_window?.__POTALE_RELEASE_ID__,
            120
        ),
    };

    if (!payload.message || is_duplicate_error(payload)) {
        return;
    }

    void send_frontend_log(payload);
};

const install_frontend_logging = () => {
    if (!browser_window || frontend_logging_installed) {
        return;
    }

    frontend_logging_installed = true;

    browser_window.addEventListener('error', (event) => {
        const target = event.target;
        const asset_path = target instanceof HTMLScriptElement
            ? target.src
            : target instanceof HTMLLinkElement
                ? target.href
                : '';
        const error = event.error || new Error(
            asset_path
                ? `Не загрузился ресурс: ${asset_path}`
                : event.message || 'Необработанная ошибка окна'
        );

        log_frontend_error('window_error', error, {
            request_path: asset_path,
        });
    }, true);

    browser_window.addEventListener('unhandledrejection', (event) => {
        log_frontend_error(
            'unhandled_rejection',
            event.reason || new Error('Необработанное отклонение Promise')
        );
    });
};


export {
    install_frontend_logging,
    log_frontend_error,
};
